/**
 * TablePagination — the shared footer pager for the web app's list pages.
 *
 * Numbered page buttons plus a "<page_size> per page / <total> total" caption,
 * collapsing to a "Showing N <noun>" caption when everything fits on one page.
 * Counts use `toLocaleString()`.
 *
 * Extracted from `routes/research/imports/ImportsPagination.tsx` on 2026-08-05
 * when My Searches needed the same control. `ImportsPagination` is now a thin
 * wrapper over this component, so the imports pager's markup, copy and
 * `data-testid` are unchanged (its unit tests and the `upload-parse-list` E2E
 * spec both assert on them).
 *
 * Design-system token utilities only — no inline `style={{}}`.
 */
export interface TablePaginationProps {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  /** Singular row noun for the single-page caption (e.g. `import`, `search`). */
  noun: string;
  /** Plural form; defaults to `noun + "s"`. */
  nounPlural?: string;
  /** Test hook, so each list keeps its own stable selector. */
  testId: string;
}

export function TablePagination({
  total,
  page,
  pageSize,
  onPageChange,
  noun,
  nounPlural,
  testId,
}: TablePaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const plural = nounPlural ?? `${noun}s`;

  // The caption-only form is for a list that FITS — but only when the caller is
  // actually on page 1. A total that shrinks under a caller sitting on a later
  // page (a filter narrowing, a poll returning fewer rows) would otherwise swap
  // the controls for a caption and strand them on an empty page with nothing to
  // click. Rendering the pager whenever `page > 1` always leaves a way back.
  if (total <= pageSize && page <= 1) {
    return (
      <div
        data-testid={testId}
        className="flex items-center justify-end px-1 py-2 text-label text-text-muted"
      >
        Showing {total.toLocaleString()} {total === 1 ? noun : plural}
      </div>
    );
  }

  // Compact numbered set: all pages when few, else a windowed list.
  const pages = buildPageList(page, pageCount);

  return (
    <div
      data-testid={testId}
      className="flex flex-wrap items-center justify-between gap-2 px-1 py-2"
    >
      <span className="text-label text-text-muted">
        {pageSize.toLocaleString()} per page / {total.toLocaleString()} total
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="rounded-md border border-border px-2 py-1 text-label text-text-secondary hover:border-border-hover disabled:opacity-40"
          aria-label="Previous page"
        >
          Prev
        </button>
        {pages.map((item) =>
          item.kind === "gap" ? (
            <span
              key={item.key}
              className="px-1 text-label text-text-muted"
              aria-hidden="true"
            >
              …
            </span>
          ) : (
            <button
              key={item.value}
              type="button"
              aria-current={item.value === page ? "page" : undefined}
              onClick={() => onPageChange(item.value)}
              className={
                item.value === page
                  ? "rounded-md border border-accent bg-accent-muted px-2.5 py-1 text-label text-accent"
                  : "rounded-md border border-border px-2.5 py-1 text-label text-text-secondary hover:border-border-hover"
              }
            >
              {item.value}
            </button>
          )
        )}
        <button
          type="button"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
          className="rounded-md border border-border px-2 py-1 text-label text-text-secondary hover:border-border-hover disabled:opacity-40"
          aria-label="Next page"
        >
          Next
        </button>
      </div>
    </div>
  );
}

type PageItem =
  | { kind: "page"; value: number }
  | { kind: "gap"; key: string };

/** Windowed page list: first, last, current ±1, ellipses for gaps. Each gap
 *  carries a stable content-derived key (the page it follows) so React never
 *  keys on an array index. */
function buildPageList(page: number, pageCount: number): PageItem[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => ({
      kind: "page" as const,
      value: i + 1,
    }));
  }
  const set = new Set<number>([1, pageCount, page, page - 1, page + 1]);
  const sorted = [...set].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);
  const out: PageItem[] = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p - prev > 1) out.push({ kind: "gap", key: `gap-after-${prev}` });
    out.push({ kind: "page", value: p });
    prev = p;
  }
  return out;
}
